import React, { useState, useRef } from 'react';
import RemixSubmenu from './RemixSubmenu';
import { MoreVerticalIcon } from '../../utils/icons';
import colors from '../../utils/colors';

// Sound Wave Animation Component
const SoundWave = () => (
  <div className="flex items-center justify-center gap-0.5">
    <div className="w-0.5 bg-white rounded-full" style={{ 
      height: '8px',
      animation: 'soundWave1 1.2s ease-in-out infinite'
    }} />
    <div className="w-0.5 bg-white rounded-full" style={{ 
      height: '6px',
      animation: 'soundWave2 1.2s ease-in-out infinite'
    }} />
    <div className="w-0.5 bg-white rounded-full" style={{ 
      height: '10px',
      animation: 'soundWave3 1.2s ease-in-out infinite'
    }} />
    <div className="w-0.5 bg-white rounded-full" style={{ 
      height: '7px',
      animation: 'soundWave4 1.2s ease-in-out infinite'
    }} />
  </div>
);

const PlaylistCard = ({ playlist, onRemixClick, onPlaySong, currentSong, isPlaying }) => {
  const [showRemixSubmenu, setShowRemixSubmenu] = useState({});
  const [hoveredSongIndex, setHoveredSongIndex] = useState(null);
  const remixButtonRefs = useRef({});
  const submenuRefs = useRef({});

  const handleSongRemixClick = (e, songIndex) => {
    e.stopPropagation();
    setShowRemixSubmenu(prev => ({
      ...prev,
      [songIndex]: !prev[songIndex]
    }));
  };

  const handleRemixAction = (action, song) => {
    console.log(`Remix action: ${action} for song:`, song);
    setShowRemixSubmenu({});
    onRemixClick?.(action, song);
  };

  return (
    <div className="flex flex-col justify-start items-start w-[345px] gap-2.5 px-3 py-[15px] rounded-[20px] bg-white/[0.04]">
      {/* Header section with main playlist info and large play button */}
      <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-3 p-2 rounded-2xl">
        <div className="flex-grow-0 flex-shrink-0 w-[100px] h-[100px] relative">
          <img
            className="w-[100px] h-[100px] absolute left-0 top-0 rounded-[10px] object-cover"
            src={playlist.image}
            alt={playlist.title}
          />
          {/* Large play button overlay */}
          <div className="w-10 h-10 absolute left-[30px] top-[30px] cursor-pointer">
            <svg
              width={40}
              height={40}
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-10 h-10"
              preserveAspectRatio="none"
            >
              <g filter="url(#filter0_b_0_456_31324)">
                <circle cx={20} cy={20} r={20} fill="black" fillOpacity="0.3" />
              </g>
              <path
                d="M14 26.7045V13.2955C14 12.9285 14.1383 12.6208 14.4148 12.3725C14.6914 12.1242 15.014 12 15.3827 12C15.4979 12 15.6189 12.0162 15.7457 12.0486C15.8724 12.081 15.9934 12.1296 16.1086 12.1943L27.3778 18.8988C27.5852 19.0283 27.7407 19.1903 27.8444 19.3846C27.9481 19.5789 28 19.7841 28 20C28 20.2159 27.9481 20.4211 27.8444 20.6154C27.7407 20.8097 27.5852 20.9717 27.3778 21.1012L16.1086 27.8057C15.9934 27.8704 15.8724 27.919 15.7457 27.9514C15.6189 27.9838 15.4979 28 15.3827 28C15.014 28 14.6914 27.8758 14.4148 27.6275C14.1383 27.3792 14 27.0715 14 26.7045Z"
                fill="#FAF7F5"
              />
              <defs>
                <filter
                  id="filter0_b_0_456_31324"
                  x={-84}
                  y={-84}
                  width={208}
                  height={208}
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity={0} result="BackgroundImageFix" />
                  <feGaussianBlur in="BackgroundImageFix" stdDeviation={52} />
                  <feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_0_456_31324" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_backgroundBlur_0_456_31324" result="shape" />
                </filter>
              </defs>
            </svg>
          </div>
        </div>
        
        {/* Playlist title and metadata */}
        <div className="flex flex-col justify-start items-start flex-grow gap-1">
          <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
            <p className="flex-grow-0 flex-shrink-0 text-base font-semibold text-left text-[#faf7f5]">
              {playlist.title}
            </p>
          </div>
          <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
            <p className="self-stretch flex-grow-0 flex-shrink-0 w-[193px] text-xs text-left text-[#d1cbc7]">
              {playlist.genres}
            </p>
            <p className="self-stretch flex-grow-0 flex-shrink-0 w-[193px] opacity-40 text-xs text-left text-[#d1cbc7]">
              {playlist.songCount} songs
            </p>
          </div>
        </div>
      </div>

      {/* Song list section */}
      <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-[5px]">
        {playlist.songs.map((song, index) => {
          const isHovered = hoveredSongIndex === index;
          const isSongPlaying = currentSong?.id === song.id && isPlaying;
          
          return (
            <div 
              key={index} 
              className="flex justify-between items-center self-stretch flex-grow-0 flex-shrink-0 h-[86px] p-2 rounded-2xl relative cursor-pointer transition-colors duration-200"
              onMouseEnter={() => setHoveredSongIndex(index)}
              onMouseLeave={() => setHoveredSongIndex(null)}
              onClick={() => onPlaySong?.(song)}
            >
              {/* Hover overlay fog */}
              {isHovered && (
                <div 
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(0.5px)'
                  }}
                />
              )}
              
              <div className="flex justify-start items-center self-stretch flex-grow relative gap-3 z-10">
                {/* Song artwork */}
                <div 
                  className="flex-grow-0 flex-shrink-0 w-[52px] h-[70px] relative overflow-hidden rounded-xl"
                  style={{
                    backgroundImage: `url(${song.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Duration badge */}
                  <div className="flex justify-center items-center h-5 absolute left-3.5 top-[46px] opacity-0 overflow-hidden gap-2.5 p-1 rounded-[100px] bg-black/10 backdrop-blur-2xl">
                    <p className="flex-grow-0 flex-shrink-0 text-[10px] text-left text-white">
                      {song.duration}
                    </p>
                  </div>

                  {/* Play button overlay or sound waves */}
                  {isSongPlaying ? (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <SoundWave />
                    </div>
                  ) : isHovered && (
                    <div 
                      className="absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-200 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlaySong?.(song);
                      }}
                    >
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.33 12.72V3.28C3.33 2.95 3.43 2.68 3.62 2.48C3.81 2.28 4.05 2.19 4.39 2.19C4.49 2.19 4.61 2.20 4.75 2.24C4.88 2.28 5.00 2.33 5.11 2.40L12.69 7.12C12.85 7.23 12.96 7.36 13.03 7.51C13.09 7.66 13.13 7.81 13.13 8.00C13.13 8.19 13.09 8.35 13.03 8.49C12.96 8.64 12.85 8.77 12.69 8.88L5.11 13.60C5.00 13.67 4.88 13.72 4.75 13.76C4.61 13.80 4.49 13.81 4.39 13.81C4.05 13.81 3.81 13.72 3.62 13.52C3.43 13.32 3.33 13.05 3.33 12.72Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  )}
                </div>

              {/* Song info */}
              <div className="flex flex-col justify-center items-start flex-grow-0 flex-shrink-0 gap-2">
                <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                  <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 relative gap-2">
                    <p className="flex-grow-0 flex-shrink-0 text-sm font-medium text-left text-[#f7f4ef]">
                      {song.title}
                    </p>
                    <div className="flex justify-start items-start flex-grow-0 flex-shrink-0 relative opacity-0 overflow-hidden gap-2 px-1.5 py-0.5 rounded-[34px] bg-[#fd429c]/10">
                      <p className="flex-grow-0 flex-shrink-0 text-xs text-left text-[#fd429c]">v4.5+</p>
                    </div>
                  </div>
                  <p className="flex-grow-0 flex-shrink-0 text-xs text-left text-neutral-400">
                    {song.genres}
                  </p>
                </div>

                {/* Artist and play count */}
                <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-2">
                  <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1">
                    <img className="flex-grow-0 flex-shrink-0 w-3 h-3 rounded-full" src={song.avatar} alt={song.artist} />
                    <p className="flex-grow-0 flex-shrink-0 text-xs text-center text-[#f7f4ef]">
                      {song.artist}
                    </p>
                  </div>
                  <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-0.5">
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="flex-grow-0 flex-shrink-0 w-4 h-4 relative"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path
                        d="M3.3335 12.4695V3.5302C3.3335 3.28549 3.42568 3.08036 3.61004 2.91482C3.7944 2.74927 4.00949 2.6665 4.25531 2.6665C4.33212 2.6665 4.41278 2.6773 4.49728 2.69889C4.58178 2.72049 4.66244 2.75287 4.73926 2.79606L12.252 7.26569C12.3903 7.35206 12.494 7.46003 12.5631 7.58958C12.6323 7.71914 12.6668 7.85589 12.6668 7.99984C12.6668 8.14379 12.6323 8.28054 12.5631 8.41009C12.494 8.53965 12.3903 8.64761 12.252 8.73398L4.73926 13.2036C4.66244 13.2468 4.58178 13.2792 4.49728 13.3008C4.41278 13.3224 4.33212 13.3332 4.25531 13.3332C4.00949 13.3332 3.7944 13.2504 3.61004 13.0849C3.42568 12.9193 3.3335 12.7142 3.3335 12.4695Z"
                        fill="#A3A3A3"
                      />
                    </svg>
                    <p className="flex-grow-0 flex-shrink-0 text-xs text-center text-neutral-400">
                      {song.playCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

              {/* Three dot menu - positioned absolute to right, only show on hover */}
              {isHovered && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-20">
                  <button
                    ref={el => remixButtonRefs.current[index] = el}
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                    onClick={(e) => handleSongRemixClick(e, index)}
                  >
                    <MoreVerticalIcon className="w-4 h-4" style={{ color: colors.text.secondary }} />
                  </button>

                  {/* Remix Submenu */}
                  {showRemixSubmenu[index] && (
                    <div 
                      ref={el => submenuRefs.current[index] = el}
                      className="absolute top-0 right-8 z-50"
                    >
                      <RemixSubmenu
                        onOpenEditor={() => handleRemixAction('openEditor', song)}
                        onCover={() => handleRemixAction('cover', song)}
                        onKaraoke={() => handleRemixAction('karaoke', song)}
                        onExtend={() => handleRemixAction('extend', song)}
                        onIntro={() => handleRemixAction('intro', song)}
                        onSpeed={() => handleRemixAction('speed', song)}
                        onReplaceSection={() => handleRemixAction('replaceSection', song)}
                        onReuseLyrics={() => handleRemixAction('reuseLyrics', song)}
                        onClose={() => setShowRemixSubmenu(prev => ({ ...prev, [index]: false }))}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* See More button */}
      <div className="flex justify-center items-center self-stretch flex-grow-0 flex-shrink-0 h-10 relative gap-1 px-4 rounded-[100px] bg-white/[0.04]">
        <p className="flex-grow-0 flex-shrink-0 text-xs font-medium text-left text-white/50">
          See More
        </p>
      </div>
    </div>
  );
};

export default PlaylistCard;